import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { HowItWorks } from "../components/IntroModal";
import { openCookieSettings } from "../cookies/consent";
import { useAuth, useGetMatches, useGetProfile } from "../hooks";
import { APP_HOME_PATH } from "../lib/app-paths";
import { formatDate, formatTime } from "../lib/format";
import logo from "../match-response/logo.png";

const MAX_PLAYERS = 4;

export const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: matches = [], isLoading, error, refetch } = useGetMatches();
  const { user, isAuthenticated } = useAuth();
  const { data: profile } = useGetProfile(user?.id);
  const initial = profile?.name ? profile.name.charAt(0).toUpperCase() : "U";
  const hasMatches = !isLoading && !error && matches.length > 0;

  return (
    <main className={hasMatches ? "pp-screen pp-screen--fab" : "pp-screen"}>
      <header className="pp-topbar">
        <Link to={APP_HOME_PATH} className="pp-brand">
          <img src={logo} alt="" />
          pinpadel
        </Link>
        <button
          type="button"
          className="pp-avatar"
          aria-label={
            isAuthenticated ? t("common.settings") : t("common.signIn")
          }
          onClick={() => navigate(isAuthenticated ? "/ajustes" : "/login")}
        >
          {isAuthenticated ? initial : "👤"}
        </button>
      </header>

      {isLoading && <p className="pp-status">{t("home.loadingMatches")}</p>}

      {!isLoading && error && (
        <section className="pp-empty">
          <div className="pp-emoji">⚠️</div>
          <h1 className="pp-title">{t("common.error")}</h1>
          <p className="pp-subtitle">{t("home.loadError")}</p>
          <div className="pp-actions">
            <button type="button" className="pp-btn" onClick={() => refetch()}>
              {t("common.retry")}
            </button>
          </div>
        </section>
      )}

      {!isLoading && !error && matches.length === 0 && (
        <section className="pp-empty">
          <div className="pp-emoji">🎾</div>
          <h1 className="pp-title">{t("home.emptyTitle")}</h1>
          <p className="pp-subtitle">{t("home.emptyDescription")}</p>
          <div className="pp-card pp-steps-card">
            <HowItWorks />
          </div>
          <div className="pp-actions">
            <button
              type="button"
              className="pp-btn"
              onClick={() => navigate("/crear")}
            >
              {t("home.createMatch")}
            </button>
          </div>
        </section>
      )}

      {hasMatches && (
        <>
          <div className="pp-list">
            {matches.map((match) => {
              const missing = match.spotsNeeded;
              const isComplete = missing === 0;
              const confirmed = MAX_PLAYERS - missing;
              const progress = (confirmed / MAX_PLAYERS) * 100;

              return (
                <Link
                  key={match.id}
                  to={`/partido/${match.id}`}
                  className="pp-card pp-card-link"
                >
                  <div className="pp-row">
                    <div>
                      <h2>{match.location}</h2>
                      <p className="pp-muted">
                        📅 {formatDate(match.date)} • ⏰{" "}
                        {formatTime(match.time)}
                      </p>
                    </div>
                    {isComplete && (
                      <span className="pp-badge">{t("home.complete")}</span>
                    )}
                  </div>
                  {!isComplete && (
                    <>
                      <div className="pp-row" style={{ marginTop: 12 }}>
                        <strong>
                          {t("common.missing", { count: missing })}
                        </strong>
                        <span className="pp-muted">
                          {confirmed}/{MAX_PLAYERS}
                        </span>
                      </div>
                      <div className="pp-progress">
                        <span style={{ width: `${progress}%` }} />
                      </div>
                    </>
                  )}
                </Link>
              );
            })}
          </div>
          <button
            type="button"
            className="pp-btn pp-fab"
            onClick={() => navigate("/crear")}
          >
            {t("home.createMatch")}
          </button>
        </>
      )}

      <footer className="pp-footer-links">
        <Link to="/privacy" className="pp-footer-link">
          {t("common.privacy")}
        </Link>
        <button
          type="button"
          className="pp-footer-link"
          onClick={openCookieSettings}
        >
          {t("cookies.manage")}
        </button>
      </footer>
    </main>
  );
};
